<template>
    <span>
        <slot name="trigger" :show="show">
            <r-button @click="show" :disabled="isProcessing" fluid>Redeem Coupon</r-button>
        </slot>
        <v-modal title="Redeem Coupon" @close="closeAndReset" v-if="showModal">
        <div slot="contents">
            <div class="modal-content">
                <div class="grid-content">
                    <div class="grid-item grid-item-full">
                        <v-error-messages
                            :has-frontend-errors="$v.$error"
                            class="stack-l">
                            <li class="list-item" v-if="!$v.appliedCoupons.required">Please select at least one coupon.</li>
                        </v-error-messages>
                        <v-select
                            :loading="isSearching"
                            @search-change="findCoupon"
                            @option-select="selectCoupon"
                            :validate="$v.appliedCoupons"
                            :options="coupons"/>
                    </div>
                </div>
            </div>
            <div class="modal-content" v-if="hasAppliedCoupons">
                <template v-for="(coupon, index) in appliedCoupons">
                    <div class="grid-fitted"
                         :key="index">
                        <div class="grid-item">
                            <strong>Coupon {{index + 1}} - {{getDiscountSummary(coupon)}} Discount</strong>
                        </div>
                        <div class="grid-item no-flex">
                            <v-icon-button
                                @click="deleteCoupon(index)"
                                type="basic"
                                class="no-padding"
                                tooltip="Delete">
                                <v-icon icon="trash"/>
                            </v-icon-button>
                        </div>
                        <div class="grid-item grid-item-full">
                            <p class="text-muted">{{coupon.redemptionCode}}</p>
                            <p class="text-muted" v-if="coupon.description">{{coupon.description}}</p>
                        </div>
                    </div>
                    <div class="divider" v-if="moreThanOneCoupon && isNotLastCoupon(index)"></div>
                </template>
            </div>
            <div class="modal-content">
                <v-inline-notifications type="warning" permanent class="stack-m" v-if="!hasPrimaryEmailAddress">
                    <p slot="content">User does not have a primary email address. Please provide one in order to send an email.</p>
                </v-inline-notifications>
                <v-checkbox
                    label="Send email to customer"
                    v-model="sendEmail"
                    :disabled="!hasPrimaryEmailAddress"
                    :value="true"
                    caption="Notify the customer by email that coupons were redeemed."
                />
            </div>
        </div>
        <template slot="actions">
            <r-button type="primary" @click="redeem" :loading="isProcessing">Redeem</r-button>
        </template>
    </v-modal>
    </span>
</template>

<script>
    import {
        vCheckbox, vIcon, vIconButton, vSelect, vErrorMessages,
    } from '@/components/form';
    import CouponModel from '@/common/models/coupon-model';
    import CouponRedemptionModel from '@/common/models/coupon-redemption-model';
    import CustomerModel from '@/common/models/customer-model';
    import OrderModel from '@/common/models/order-model';
    import vModal from '@/components/modal/v-modal';
    import vInlineNotifications from '@/components/v-inline-notifications';
    import {required, minLength} from 'vuelidate/lib/validators';
    import _ from '@/common/helpers';

    export default {
        name: 'redeem-coupon',
        inject: ['sendEmailToCustomer'],
        components: {
            vModal,
            vSelect,
            vCheckbox,
            vErrorMessages,
            vInlineNotifications,
            vIconButton,
            vIcon,
        },
        props: {
            order: {
                type: OrderModel,
            },
            customer: {
                type: CustomerModel,
            },
        },
        computed: {
            hasAppliedCoupons() {
                return this.appliedCoupons.length > 0;
            },
            moreThanOneCoupon() {
                return this.appliedCoupons.length > 1;
            },
            hasOrder() {
                return !!this.order;
            },
            couponCustomer() {
                return this.hasOrder && this.order.customer
                    ? this.order.customer
                    : this.customer;
            },
            hasPrimaryEmailAddress() {
                return this.customer.primaryAddress.primaryEmailAddress;
            },
        },
        data() {
            return {
                showModal: false,
                sendEmail: false,
                isSearching: false,
                coupons: [],
                appliedCoupons: [],
                searchTimer: null,
                isProcessing: false,
            };
        },
        validations: {
            appliedCoupons: {
                required,
                minLength: minLength(1),
            },
        },
        methods: {
            show() {
                this.showModal = true;
            },
            async redeem() {
                this.$v.$touch();
                if (!this.$v.appliedCoupons.$error) {
                    const couponCount = this.appliedCoupons.length;
                    try {
                        this.isProcessing = true;
                        const redemptions = this.appliedCoupons.map(coupon => this.$api(({coupons}) => {
                            const data = new CouponRedemptionModel({
                                redemptionCode: coupon.redemptionCode,
                                customerId: this.couponCustomer.id,
                            }).getPayload();
                            return coupons.redeem({data});
                        }));
                        if (this.sendEmail) {
                            // notify the customer that coupon(s) were applied
                            this.sendEmailToCustomer({
                                subject: `${couponCount === 1 ? 'A coupon was' : 'Coupons were'} added to your account`,
                                text: [
                                    `Dear ${this.couponCustomer.displayName},`,
                                    `We have added ${couponCount} ${_.pluralize('coupon', couponCount)} (${this.appliedCoupons.map(v => v.redemptionCode).join(', ')})${this.hasOrder ? ` to your account for order (#${this.order.id})` : ''}.`,
                                    `Thank you`,
                                ].join('\n\n'),
                            });
                        }
                        await Promise.all(redemptions);
                        this.$toast.positive(`${_.pluralize('Coupon', couponCount)} redeemed successfully.`);
                    } catch (error) {
                        this.$toast.fromError(error, `Unable to redeem the ${_.pluralize('Coupon', couponCount)} at this time.`);
                    } finally {
                        this.isProcessing = false;
                        this.closeAndReset();
                    }
                }
            },
            findCoupon() {
                // show loading
                this.isSearching = true;
                // empty previous results
                this.coupons = [];
                clearTimeout(this.searchTimer);
                this.searchTimer = setTimeout(async () => {
                    await this.queryCoupons();
                    this.isSearching = false;
                }, 200);
            },
            async queryCoupons() {
                const params = {filter: `status:${CouponModel.CouponStatus.issued}`};
                const {items} = await this.$api(({coupons}) => coupons.getAll(params));
                this.coupons = items.map(({fields}) => ({
                    label: fields.redemptionCode,
                    value: fields.redemptionCode,
                    data: {...fields},
                }));
            },
            selectCoupon({data}) {
                this.appliedCoupons.push(new CouponModel(data));
            },
            deleteCoupon(index) {
                this.appliedCoupons.splice(index, 1);
            },
            closeAndReset() {
                this.$v.$reset();
                this.showModal = false;
                this.sendEmail = false;
                this.appliedCoupons = [];
                this.$emit('close');
            },
            isNotLastCoupon(index) {
                return index !== this.appliedCoupons.length - 1;
            },
            getDiscountSummary({discount}) {
                if (discount.type === CouponModel.DiscountTypes.percent) {
                    return `${discount.value}%`;
                }
                return _.monetizeExplicit(discount.amount, discount.currency);
            },
        },
        async mounted() {
            await this.queryCoupons(null);
        },
    };
</script>
